import { useEffect, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import TimesheetCalendar from "../components/TimesheetCalendar";
import ConversationSheet from "../components/ConversationSheet";
import { StatusPill, MonthlySummary, Legend } from "./EmployeeDashboard";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Download, Eye } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useNavigate, useParams } from "react-router-dom";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

async function downloadFile(url, filename) {
  const token = localStorage.getItem("dataeko_token");
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { toast.error("Download failed"); return; }
  const blob = await res.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = filename; a.click();
}

export function AccountsList() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  useEffect(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/timesheets${q}`).then(r => setItems(r.data));
  }, [filter]);

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />
      <div className="p-6 lg:p-10 max-w-[1400px] mx-auto fade-up">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-blue-600 dark:text-blue-400 mb-2">— Accounts (Read-only)</p>
        <h1 className="font-display text-4xl tracking-tight font-light mb-8">All Timesheets</h1>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Label className="text-xs text-muted-foreground">Status:</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 bg-background border-border" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="clarification">Clarification</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="accounts-list-table">
            <thead className="bg-muted/40 text-muted-foreground text-[11px] uppercase tracking-[0.18em] font-mono">
              <tr>
                <th className="text-left px-4 py-3">Employee</th>
                <th className="text-left px-4 py-3">Period</th>
                <th className="text-left px-4 py-3">Days</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div>{t.user?.name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{t.user?.email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono">{t.year}-{String(t.month).padStart(2,"0")}</td>
                  <td className="px-4 py-3 font-display text-blue-600 dark:text-blue-400">{t.summary?.working_days || 0}</td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <Button variant="ghost" size="sm" onClick={() => downloadFile(`/api/export/timesheet/${t.id}`, `${t.id}.xlsx`)} data-testid={`download-${t.id}`}><Download className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => nav(`/accounts/view/${t.user_id}/${t.year}/${t.month}`)} data-testid={`view-${t.id}`}><Eye className="h-4 w-4 mr-1" /> View</Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No timesheets</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export function AccountsExports() {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(0);

  const exportAll = () => {
    const params = [];
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    const q = params.length ? `?${params.join("&")}` : "";
    downloadFile(`/api/export/all${q}`, `dataeko_timesheets_${year}_${month || "all"}.xlsx`);
  };

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />
      <div className="p-6 lg:p-10 max-w-[900px] mx-auto fade-up">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-blue-600 dark:text-blue-400 mb-2">— Exports</p>
        <h1 className="font-display text-4xl tracking-tight font-light mb-8">Excel Downloads</h1>

        <div className="rounded-xl border border-border bg-card p-6 space-y-5">
          <h2 className="font-display text-xl">Bulk Export — all employees</h2>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div>
              <Label>Year</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="bg-background border-border" data-testid="export-year"><SelectValue /></SelectTrigger>
                <SelectContent>{[2026,2027,2028].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Month (optional)</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="bg-background border-border" data-testid="export-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">All months</SelectItem>
                  {MONTHS.map((m,i) => <SelectItem key={m} value={String(i+1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={exportAll} className="bg-blue-500 hover:bg-blue-400 text-white" data-testid="export-all-button"><Download className="h-4 w-4 mr-1.5" /> Download Excel</Button>
        </div>
      </div>
    </Layout>
  );
}

export function AccountsView() {
  const { userId, year, month } = useParams();
  const [ts, setTs] = useState(null);
  const [convoOpen, setConvoOpen] = useState(false);

  useEffect(() => { api.get(`/timesheet/${userId}/${year}/${month}`).then(r => setTs(r.data)); }, [userId, year, month]);

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />
      <div className="p-6 lg:p-10 max-w-[1400px] mx-auto fade-up">
        {ts && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-blue-600 dark:text-blue-400 mb-2">— Read-Only View</p>
                <h1 className="font-display text-4xl tracking-tight font-light">{ts.user?.name}</h1>
                <p className="text-muted-foreground font-mono text-sm">{ts.user?.email} · {year}-{String(month).padStart(2,"0")}</p>
              </div>
              <div className="flex gap-2">
                <StatusPill status={ts.status} />
                <Button variant="outline" onClick={() => downloadFile(`/api/export/timesheet/${ts.id}`, `${ts.id}.xlsx`)} data-testid="view-download"><Download className="h-4 w-4 mr-1.5" /> Excel</Button>
                <Button onClick={() => setConvoOpen(true)} variant="outline" data-testid="view-conversation">Conversation</Button>
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
              <TimesheetCalendar year={Number(year)} month={Number(month)} entries={ts.entries} onChange={() => {}} locked />
              <aside className="space-y-4">
                <MonthlySummary summary={ts.summary} />
                <Legend />
              </aside>
            </div>
            <ConversationSheet open={convoOpen} onOpenChange={setConvoOpen} ts={ts} onUpdated={() => {}} role="accounts" />
          </>
        )}
      </div>
    </Layout>
  );
}
